import type {
  AudioVideoObserver,
  MeetingSessionStatus,
  VideoDownlinkObserver,
  VideoPriorityBasedPolicy,
  VideoSource,
} from "amazon-chime-sdk-js";
import type { RosterType } from "amazon-chime-sdk-component-library-react";
import type {
  ConnectionQuality,
  TileState,
  VideoSourceState,
} from "@core/features/video";
import {
  Layout,
  isContentShare,
  updateDownlinkPreferences,
  videoEventBus,
  GRACE_GOOD_MS,
  GRACE_POOR_MS,
  ROSTER_GRACE_MS,
  RECOVERY_COOLDOWN_MS,
  RECONNECT_GUARD_TIMEOUT_MS,
  MASS_DROP_VALIDATION_MS,
  INTENT_STALENESS_MS,
} from "@core/features/video";
import { resolveAttendeeTileState } from "./resolveAttendeeTileState";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DeviceStateMessage {
  camera: boolean;
  mic: boolean;
  timestamp: number;
}

interface IntentData {
  cameraIntentOn: boolean;
  micIntentOn: boolean;
  lastUpdate: number;
}

/** Store interface — the subset of platform store that ChimeObserverState needs. */
export interface ChimeObserverStore {
  // reads
  attendeeTileState: Record<string, TileState>;
  videoSourceState: VideoSourceState;
  layout: Layout;
  user: { id: number } | null;
  // writes — individual
  setAttendeeTileState: (id: string, state: TileState) => void;
  setAttendeeTileStates: (states: Record<string, TileState>) => void;
  removeAttendeeTileState: (id: string) => void;
  setConnectionQuality: (q: ConnectionQuality) => void;
  setActiveSpeakers: (speakers: string[]) => void;
  setRoster: (roster: RosterType) => void;
  // writes — atomic batch (for remoteVideoSourcesDidChange)
  setState: (fn: (s: any) => Partial<any>) => void;
}

// ---------------------------------------------------------------------------
// State machine class
// ---------------------------------------------------------------------------

export class ChimeObserverState {
  // --- Instance fields (formerly refs) ---
  sdkSourceSet = new Set<string>();
  tileAttendeeMap = new Map<number, string>();
  intentData = new Map<string, IntentData>();
  graceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  rosterTimers = new Map<string, ReturnType<typeof setTimeout>>();
  hadVideoSet = new Set<string>();
  bandwidthPaused = new Set<string>();

  connectionQuality: ConnectionQuality = 'good';
  isReconnecting = false;
  pendingSources: VideoSource[] | null = null;
  reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  massDropTimer: ReturnType<typeof setTimeout> | null = null;
  recoveryTimer: ReturnType<typeof setTimeout> | null = null;

  previousActiveSpeakers: string[] = [];

  constructor(
    private getStore: () => ChimeObserverStore,
    public getLocalAttendeeId: () => string | null
  ) {}

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /** Transition a single attendee's tile state, cancelling any grace timer. */
  transitionAttendee(attendeeId: string, state: TileState): void {
    const graceTimer = this.graceTimers.get(attendeeId);
    if (graceTimer) {
      clearTimeout(graceTimer);
      this.graceTimers.delete(attendeeId);
    }
    if (state === 'video') {
      this.hadVideoSet.add(attendeeId);
    }
    this.getStore().setAttendeeTileState(attendeeId, state);
  }

  /** Start a grace timer for an attendee — fires 'avatar' after graceMs. */
  startGraceTimer(attendeeId: string): void {
    const existing = this.graceTimers.get(attendeeId);
    if (existing) clearTimeout(existing);

    const graceMs = this.connectionQuality === 'good' ? GRACE_GOOD_MS : GRACE_POOR_MS;

    this.graceTimers.set(
      attendeeId,
      setTimeout(() => {
        this.graceTimers.delete(attendeeId);
        // Re-check: attendee may have reappeared during grace period
        if (this.sdkSourceSet.has(attendeeId)) return;
        if (this.connectionQuality !== 'good') return; // degraded — stay frozen
        this.getStore().setAttendeeTileState(attendeeId, 'avatar');
      }, graceMs)
    );
  }

  /** Remove all internal state for a departed attendee. */
  purgeAttendee(attendeeId: string): void {
    this.rosterTimers.delete(attendeeId);
    this.intentData.delete(attendeeId);
    this.hadVideoSet.delete(attendeeId);
    this.bandwidthPaused.delete(attendeeId);
    const graceTimer = this.graceTimers.get(attendeeId);
    if (graceTimer) {
      clearTimeout(graceTimer);
      this.graceTimers.delete(attendeeId);
    }
    this.getStore().removeAttendeeTileState(attendeeId);
  }

  /**
   * Subscribe to attendee presence changes directly from the SDK.
   * This fires synchronously — before remoteVideoSourcesDidChange — ensuring
   * the roster timer gate is set before any source-change callbacks process
   * the departing attendee.
   */
  subscribeToPresence(audioVideo: {
    realtimeSubscribeToAttendeeIdPresence: (cb: (id: string, present: boolean, extId?: string, dropped?: boolean) => void) => void;
    realtimeUnsubscribeToAttendeeIdPresence: (cb: (id: string, present: boolean, extId?: string, dropped?: boolean) => void) => void;
  }): () => void {
    const handler = (
      attendeeId: string,
      present: boolean,
      _externalUserId?: string,
      _dropped?: boolean
    ) => {
      const localId = this.getLocalAttendeeId();
      if (attendeeId === localId) return;

      if (!present && !this.rosterTimers.has(attendeeId)) {
        const store = this.getStore();
        const { attendeeTileState } = store;
        const currentState = attendeeTileState[attendeeId];
        if (!currentState || currentState === 'absent') return;

        // Immediate avatar + grace timer cleanup + roster timer
        if (currentState === 'video' || currentState === 'frozen') {
          store.setAttendeeTileState(attendeeId, 'avatar');
        }
        const graceTimer = this.graceTimers.get(attendeeId);
        if (graceTimer) {
          clearTimeout(graceTimer);
          this.graceTimers.delete(attendeeId);
        }
        this.rosterTimers.set(
          attendeeId,
          setTimeout(() => this.purgeAttendee(attendeeId), ROSTER_GRACE_MS)
        );
      }

      if (present && this.rosterTimers.has(attendeeId)) {
        // Attendee returned — cancel removal timer
        const timer = this.rosterTimers.get(attendeeId);
        if (timer) clearTimeout(timer);
        this.rosterTimers.delete(attendeeId);
        // Restore to video if source available
        if (this.sdkSourceSet.has(attendeeId)) {
          this.transitionAttendee(attendeeId, 'video');
        }
      }
    };

    audioVideo.realtimeSubscribeToAttendeeIdPresence(handler);
    return () => audioVideo.realtimeUnsubscribeToAttendeeIdPresence(handler);
  }

  /** Process a single attendee's state based on current inputs. */
  processAttendee(attendeeId: string, currentState: TileState): void {
    if (this.rosterTimers.has(attendeeId)) return;
    const sourcePresent = this.sdkSourceSet.has(attendeeId);
    const intent = this.intentData.get(attendeeId);
    const intentFresh =
      intent !== undefined &&
      Date.now() - intent.lastUpdate <= INTENT_STALENESS_MS;
    const nextState = resolveAttendeeTileState({
      sourcePresent,
      intentCameraOn: intent?.cameraIntentOn,
      intentFresh,
      connectionQuality: this.connectionQuality,
      bandwidthPaused: this.bandwidthPaused.has(attendeeId),
      hadVideo: this.hadVideoSet.has(attendeeId),
    });

    if (nextState === currentState) return;

    // If transitioning to 'avatar' and had video, use grace timer
    if (
      nextState === 'avatar' &&
      this.hadVideoSet.has(attendeeId) &&
      !(intent?.cameraIntentOn === false && intentFresh)
    ) {
      // Don't start grace if connection is degraded — stay frozen
      if (this.connectionQuality !== 'good') {
        this.transitionAttendee(attendeeId, 'frozen');
      } else {
        // Set to frozen during grace, then timer fires avatar
        this.transitionAttendee(attendeeId, 'frozen');
        this.startGraceTimer(attendeeId);
      }
      return;
    }

    this.transitionAttendee(attendeeId, nextState);
  }

  // -------------------------------------------------------------------------
  // Reconnect guard
  // -------------------------------------------------------------------------

  startReconnectGuard(): void {
    if (this.isReconnecting) return;
    this.isReconnecting = true;
    this.pendingSources = null;

    // Promote connection quality to 'poor' if currently good
    if (this.connectionQuality === 'good') {
      this.connectionQuality = 'poor';
      this.getStore().setConnectionQuality('poor');
    }

    // Cancel all pending grace timers — everything stays frozen during reconnect
    for (const [, timer] of this.graceTimers) {
      clearTimeout(timer);
    }
    this.graceTimers.clear();

    // Clear SDK source set — stale during disconnect
    this.sdkSourceSet = new Set();

    // Freeze all remote attendees
    const store = this.getStore();
    const { attendeeTileState } = store;
    const localId = this.getLocalAttendeeId();
    const updates: Record<string, TileState> = { ...attendeeTileState };
    for (const id in updates) {
      if (id === localId) continue;
      if (this.rosterTimers.has(id)) continue;
      if (updates[id] === 'video') {
        updates[id] = 'frozen';
      }
    }
    store.setAttendeeTileStates(updates);

    // Safety timeout
    if (this.reconnectTimeout) clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = setTimeout(() => {
      if (this.isReconnecting) {
        this.clearReconnectGuard();
      }
    }, RECONNECT_GUARD_TIMEOUT_MS);
  }

  clearReconnectGuard(): void {
    if (!this.isReconnecting) return;
    this.isReconnecting = false;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  // -------------------------------------------------------------------------
  // Roster sync (Effect 1)
  // -------------------------------------------------------------------------

  syncRoster(roster: RosterType, localAttendeeId: string | null): void {
    // Cancel roster removal timers for attendees who reappeared
    for (const attendeeId in roster) {
      const timer = this.rosterTimers.get(attendeeId);
      if (timer) {
        clearTimeout(timer);
        this.rosterTimers.delete(attendeeId);
        // Attendee returned — if they have a source, restore to 'video'
        if (this.sdkSourceSet.has(attendeeId)) {
          this.transitionAttendee(attendeeId, 'video');
        }
      }
    }

    // NOTE: Departure handling (avatar transition + roster timer) is done
    // synchronously in subscribeToPresence(). By the time this useEffect-based
    // syncRoster runs, departed attendees already have roster timers set,
    // so no departure logic is needed here.

    // Add new attendees with initial state 'avatar' (never had video)
    const store = this.getStore();
    const { attendeeTileState } = store;
    const updates: Record<string, TileState> = {};
    let hasNewAttendees = false;
    for (const attendeeId in roster) {
      if (attendeeId === localAttendeeId) continue;
      if (!(attendeeId in attendeeTileState)) {
        // If source already available (race: source arrived before roster),
        // start as 'video'. Otherwise 'avatar'.
        if (this.sdkSourceSet.has(attendeeId)) {
          updates[attendeeId] = 'video';
          this.hadVideoSet.add(attendeeId);
        } else {
          updates[attendeeId] = 'avatar';
        }
        hasNewAttendees = true;
      }
    }

    if (hasNewAttendees) {
      store.setState((s: any) => ({
        attendeeTileState: { ...s.attendeeTileState, ...updates },
      }));
    }

    // Sync roster to store
    store.setRoster(roster);
  }

  // -------------------------------------------------------------------------
  // Active speaker sync (Effect 2)
  // -------------------------------------------------------------------------

  syncActiveSpeakers(
    activeSpeakers: string[],
    roster: RosterType,
    localAttendeeId: string | null
  ): void {
    const prev = this.previousActiveSpeakers;
    const store = this.getStore();
    const user = store.user;

    const getUserIdFromAttendeeId = (attendeeId: string): string => {
      if (attendeeId === localAttendeeId) {
        return user?.id?.toString() || attendeeId;
      }
      const attendee = roster[attendeeId];
      if (attendee?.externalUserId) {
        const namePart = attendee.externalUserId.split("-TS-")[0];
        return namePart.replace("-", "_");
      }
      return attendeeId;
    };

    // Emit analytics events for speaker transitions
    for (const speaker of activeSpeakers) {
      if (!prev.includes(speaker)) {
        videoEventBus.emitEvent("videochat:active:speaker:started", {
          userId: getUserIdFromAttendeeId(speaker),
        });
      }
    }
    for (const speaker of prev) {
      if (!activeSpeakers.includes(speaker)) {
        videoEventBus.emitEvent("videochat:active:speaker:stopped", {
          userId: getUserIdFromAttendeeId(speaker),
        });
      }
    }

    store.setActiveSpeakers(activeSpeakers);
    this.previousActiveSpeakers = activeSpeakers;
  }

  // -------------------------------------------------------------------------
  // AudioVideoObserver factory (Effect 3)
  // -------------------------------------------------------------------------

  createAudioVideoObserver(
    policyRef: { current: VideoPriorityBasedPolicy }
  ): AudioVideoObserver {
    const localAttendeeId = this.getLocalAttendeeId();

    const observer: AudioVideoObserver = {
      audioVideoDidStartConnecting: (reconnecting: boolean): void => {
        if (reconnecting) this.startReconnectGuard();
      },

      audioVideoDidStop: (sessionStatus: MeetingSessionStatus): void => {
        if (!sessionStatus.isTerminal()) this.startReconnectGuard();
      },

      audioVideoDidStart: (): void => {
        const pending = this.pendingSources;
        this.pendingSources = null;
        this.clearReconnectGuard();

        if (pending) {
          // Reconcile: filter sources against active tiles
          const activeTileAttendees = new Set(
            this.tileAttendeeMap.values()
          );
          const reconciledSources = pending.filter((vs) => {
            const id = vs.attendee.attendeeId;
            return (
              activeTileAttendees.has(id) ||
              !(id in this.getStore().attendeeTileState)
            );
          });
          observer.remoteVideoSourcesDidChange?.(reconciledSources);
        }
      },

      connectionDidBecomeGood: () => {
        if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
        this.recoveryTimer = setTimeout(() => {
          this.recoveryTimer = null;
          this.connectionQuality = 'good';
          this.getStore().setConnectionQuality('good');

          // Re-evaluate all frozen attendees — some may now start grace timers
          const { attendeeTileState } = this.getStore();
          for (const attendeeId in attendeeTileState) {
            if (attendeeTileState[attendeeId] === 'frozen') {
              this.processAttendee(attendeeId, 'frozen');
            }
          }
        }, RECOVERY_COOLDOWN_MS);
      },

      connectionDidBecomePoor: () => {
        if (this.recoveryTimer) {
          clearTimeout(this.recoveryTimer);
          this.recoveryTimer = null;
        }
        this.connectionQuality = 'poor';
        this.getStore().setConnectionQuality('poor');

        // Cancel all pending grace timers — stay frozen during degraded quality
        for (const [, timer] of this.graceTimers) {
          clearTimeout(timer);
        }
        this.graceTimers.clear();
      },

      connectionDidSuggestStopVideo: () => {
        if (this.recoveryTimer) {
          clearTimeout(this.recoveryTimer);
          this.recoveryTimer = null;
        }
        this.connectionQuality = 'suggestStopVideo';
        this.getStore().setConnectionQuality('suggestStopVideo');

        for (const [, timer] of this.graceTimers) {
          clearTimeout(timer);
        }
        this.graceTimers.clear();
      },

      videoTileDidUpdate: (tileState): void => {
        if (
          tileState.tileId &&
          !tileState.localTile &&
          tileState.boundAttendeeId
        ) {
          this.tileAttendeeMap.set(
            tileState.tileId,
            tileState.boundAttendeeId
          );
        }
      },

      videoTileWasRemoved: (tileId: number): void => {
        const attendeeId = this.tileAttendeeMap.get(tileId);
        this.tileAttendeeMap.delete(tileId);
        if (!attendeeId) return;
        if (localAttendeeId && attendeeId === localAttendeeId) return;

        // Simulcast/SFU recycle — attendee still in SDK source set
        if (this.sdkSourceSet.has(attendeeId)) return;

        const { attendeeTileState } = this.getStore();
        const currentState = attendeeTileState[attendeeId];
        if (!currentState || currentState === 'absent') return;

        this.processAttendee(attendeeId, currentState);
      },

      remoteVideoSourcesDidChange: (videoSources: VideoSource[]): void => {
        if (this.isReconnecting) {
          this.pendingSources = videoSources;
          return;
        }

        const videoSourceIdSet = new Set(
          videoSources.map((vs) => vs.attendee.attendeeId)
        );
        const previousSdkSources = this.sdkSourceSet;
        this.sdkSourceSet = videoSourceIdSet;

        // Mass-drop detection
        let droppedCount = 0;
        for (const id of previousSdkSources) {
          if (!videoSourceIdSet.has(id)) droppedCount++;
        }
        const isTotalSourceLoss =
          previousSdkSources.size > 0 && videoSourceIdSet.size === 0;
        if (isTotalSourceLoss || droppedCount >= 2) {
          if (this.connectionQuality === 'good') {
            this.connectionQuality = 'poor';
            this.getStore().setConnectionQuality('poor');
            if (this.massDropTimer) clearTimeout(this.massDropTimer);
            this.massDropTimer = setTimeout(() => {
              this.massDropTimer = null;
              if (
                this.connectionQuality === 'poor' &&
                !this.isReconnecting
              ) {
                this.connectionQuality = 'good';
                this.getStore().setConnectionQuality('good');
                // Re-evaluate frozen attendees (same as connectionDidBecomeGood)
                const { attendeeTileState } = this.getStore();
                for (const id in attendeeTileState) {
                  if (attendeeTileState[id] === 'frozen') {
                    this.processAttendee(id, 'frozen');
                  }
                }
              }
            }, MASS_DROP_VALIDATION_MS);
          }
        }

        // Build cameraSources for downlink hooks
        const cameraSources: string[] = [];
        for (const attendeeId of videoSourceIdSet) {
          if (
            !isContentShare(attendeeId) &&
            attendeeId !== localAttendeeId
          ) {
            cameraSources.push(attendeeId);
          }
        }

        // Atomic update: attendeeTileState + videoSourceState
        const store = this.getStore();
        store.setState((s: any) => {
          const nextTileState: Record<string, TileState> = {
            ...s.attendeeTileState,
          };

          // Process each known attendee
          for (const attendeeId in nextTileState) {
            if (localAttendeeId && attendeeId === localAttendeeId) continue;
            if (nextTileState[attendeeId] === 'absent') continue;
            // Skip attendees in roster grace — their visual state (avatar or
            // frozen) is already correct and should not be overridden by
            // source-loss processing during teardown.
            if (this.rosterTimers.has(attendeeId)) continue;

            if (videoSourceIdSet.has(attendeeId)) {
              // Source present
              this.hadVideoSet.add(attendeeId);
              const graceTimer = this.graceTimers.get(attendeeId);
              if (graceTimer) {
                clearTimeout(graceTimer);
                this.graceTimers.delete(attendeeId);
              }
              nextTileState[attendeeId] = this.bandwidthPaused.has(attendeeId)
                ? 'frozen'
                : 'video';
            } else if (isContentShare(attendeeId)) {
              // Content share gone — remove
              delete nextTileState[attendeeId];
              this.hadVideoSet.delete(attendeeId);
            } else if (previousSdkSources.has(attendeeId)) {
              // Source just disappeared — resolve new state
              const intent = this.intentData.get(attendeeId);
              const intentFresh =
                intent !== undefined &&
                Date.now() - intent.lastUpdate <= INTENT_STALENESS_MS;

              if (intent?.cameraIntentOn === false && intentFresh) {
                nextTileState[attendeeId] = 'avatar';
                const graceTimer = this.graceTimers.get(attendeeId);
                if (graceTimer) {
                  clearTimeout(graceTimer);
                  this.graceTimers.delete(attendeeId);
                }
              } else if (intent?.cameraIntentOn === true && intentFresh) {
                nextTileState[attendeeId] = 'frozen';
              } else if (this.connectionQuality !== 'good') {
                nextTileState[attendeeId] = 'frozen';
              } else if (this.hadVideoSet.has(attendeeId)) {
                // Had video, good connection, no intent → grace timer
                nextTileState[attendeeId] = 'frozen';
                // Schedule grace timer (outside setState)
              } else {
                nextTileState[attendeeId] = 'avatar';
              }
            }
          }

          // Add new attendees from sources not yet in tile state
          for (const attendeeId of videoSourceIdSet) {
            if (!(attendeeId in nextTileState) && !isContentShare(attendeeId)) {
              this.hadVideoSet.add(attendeeId);
              nextTileState[attendeeId] = this.bandwidthPaused.has(attendeeId)
                ? 'frozen'
                : 'video';
            }
          }

          // Filter activeSpeakersWithCameraSource
          const activeSpeakersWithCameraSource =
            s.videoSourceState.activeSpeakersWithCameraSource.filter(
              (id: string) => {
                const ts = nextTileState[id];
                return ts === 'video' || ts === 'frozen';
              }
            );

          const nextVideoSourceState: VideoSourceState = {
            ...s.videoSourceState,
            cameraSources,
            activeSpeakersWithCameraSource,
          };

          return {
            attendeeTileState: nextTileState,
            videoSourceState: nextVideoSourceState,
          };
        });

        // Start grace timers for attendees that disappeared and had video
        // (must be done after setState to avoid inconsistency)
        for (const id of previousSdkSources) {
          if (videoSourceIdSet.has(id)) continue;
          if (isContentShare(id)) continue;
          if (this.rosterTimers.has(id)) continue;
          if (
            this.hadVideoSet.has(id) &&
            this.connectionQuality === 'good'
          ) {
            const intent = this.intentData.get(id);
            const intentFresh =
              intent !== undefined &&
              Date.now() - intent.lastUpdate <= INTENT_STALENESS_MS;
            // Only start grace if no fresh intent already resolved it
            if (
              !(intent?.cameraIntentOn === false && intentFresh) &&
              !(intent?.cameraIntentOn === true && intentFresh)
            ) {
              this.startGraceTimer(id);
            }
          }
        }

        // Update downlink preferences with committed state
        const committed = this.getStore();
        updateDownlinkPreferences(
          { layout: committed.layout },
          committed.videoSourceState,
          committed.attendeeTileState,
          policyRef.current
        );
      },
    };

    return observer;
  }

  // -------------------------------------------------------------------------
  // VideoDownlinkObserver factory (Effect 3)
  // -------------------------------------------------------------------------

  createDownlinkObserver(
    audioVideo: { getVideoTile: (tileId: number) => any }
  ): VideoDownlinkObserver {
    return {
      tileWillBePausedByDownlinkPolicy: (tileId: number) => {
        const attendeeId = audioVideo
          .getVideoTile(tileId)
          ?.state().boundAttendeeId;
        if (!attendeeId) return;
        this.bandwidthPaused.add(attendeeId);
        if (this.rosterTimers.has(attendeeId)) return;
        const { attendeeTileState } = this.getStore();
        if (attendeeTileState[attendeeId] === 'video') {
          this.getStore().setAttendeeTileState(attendeeId, 'frozen');
        }
      },
      tileWillBeUnpausedByDownlinkPolicy: (tileId: number) => {
        const attendeeId = audioVideo
          .getVideoTile(tileId)
          ?.state().boundAttendeeId;
        if (!attendeeId) return;
        this.bandwidthPaused.delete(attendeeId);
        if (this.rosterTimers.has(attendeeId)) return;
        if (this.sdkSourceSet.has(attendeeId)) {
          const { attendeeTileState } = this.getStore();
          if (attendeeTileState[attendeeId] === 'frozen') {
            this.getStore().setAttendeeTileState(attendeeId, 'video');
          }
        }
      },
    };
  }

  // -------------------------------------------------------------------------
  // Data message handler (Effect 4)
  // -------------------------------------------------------------------------

  handleDataMessage(message: { senderAttendeeId: string; text: () => string }): void {
    const localAttendeeId = this.getLocalAttendeeId();
    const senderId = message.senderAttendeeId;
    if (!senderId || senderId === localAttendeeId) return;

    try {
      const payload: DeviceStateMessage = JSON.parse(message.text());
      const current = this.intentData.get(senderId);

      // Timestamp ordering — keep latest, discard stale
      if (current && current.lastUpdate > payload.timestamp) return;

      this.intentData.set(senderId, {
        cameraIntentOn: payload.camera,
        micIntentOn: payload.mic,
        lastUpdate: payload.timestamp,
      });

      // Skip visual transitions for attendees in roster grace — their state
      // is locked to avatar. intentData is still updated above for reconnects.
      if (this.rosterTimers.has(senderId)) return;

      // If intent says camera on and attendee is in 'avatar', transition to
      // 'frozen' only if they previously had video (valid canvas frame exists).
      // Otherwise stay in 'avatar' and let remoteVideoSourcesDidChange
      // transition directly to 'video' when the source arrives.
      if (payload.camera) {
        const { attendeeTileState } = this.getStore();
        if (
          attendeeTileState[senderId] === 'avatar' &&
          this.hadVideoSet.has(senderId)
        ) {
          this.transitionAttendee(senderId, 'frozen');
        }
      }

      // If intent says camera off and there's a pending grace timer, fire immediately
      if (!payload.camera) {
        const graceTimer = this.graceTimers.get(senderId);
        if (graceTimer) {
          clearTimeout(graceTimer);
          this.graceTimers.delete(senderId);
          this.hadVideoSet.delete(senderId);
          this.getStore().setAttendeeTileState(senderId, 'avatar');
        } else {
          // Also transition if currently frozen and source is gone
          const { attendeeTileState } = this.getStore();
          if (
            attendeeTileState[senderId] === 'frozen' &&
            !this.sdkSourceSet.has(senderId)
          ) {
            this.hadVideoSet.delete(senderId);
            this.getStore().setAttendeeTileState(senderId, 'avatar');
          }
        }
      }

      if (process.env.NODE_ENV === 'development') {
        console.debug(
          `[ChimeObserverState] intent received from ${senderId}:`,
          `camera=${payload.camera}`,
          `mic=${payload.mic}`
        );
      }
    } catch {
      // Malformed message — ignore
    }
  }

  // -------------------------------------------------------------------------
  // Cleanup
  // -------------------------------------------------------------------------

  dispose(): void {
    for (const timer of this.graceTimers.values()) clearTimeout(timer);
    this.graceTimers.clear();
    for (const timer of this.rosterTimers.values()) clearTimeout(timer);
    this.rosterTimers.clear();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.massDropTimer) {
      clearTimeout(this.massDropTimer);
      this.massDropTimer = null;
    }
    if (this.recoveryTimer) {
      clearTimeout(this.recoveryTimer);
      this.recoveryTimer = null;
    }
  }
}
